import React, { useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Player from '@vimeo/player';
import { debounce } from 'lodash';
import { useDispatch } from 'react-redux';
import { getVideoChaptersByIdAPI } from '../../util/api';
import { fetchVideoDuration } from '../../ducks/vimeo.duck';
import { formatTime, isArrayLength, seekToChapter } from '../../util/genericHelpers';
import css from './VimeoPlayer.module.css';

const VIMEO_PLAYER_BASE_URL = process.env.REACT_APP_VIMEO_PLAYER_BASE_URL;

function VimeoPlayer({
  vimeoURL,
  onTimeUpdate,
  videoResumeTime,
  onDurationChange,
  onCurrentTimeChange,
  onPlayStart,
  onVideoEnd,
  onSeek,
  seekToTime,
}) {
  const dispatch = useDispatch();
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const hasAppliedResumeRef = useRef({}); // { [videoId]: true }
  const hasTriggeredPlayStartRef = useRef({}); // { [videoId]: true }
  const hasSignaledCompleteRef = useRef({}); // { [videoId]: true }
  const [chapters, setChapters] = useState([]);
  const [currentChapter, setCurrentChapter] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [error, setError] = useState(null);

  // Use refs for callbacks to avoid re-initialization of player
  const onPlayStartRef = useRef(onPlayStart);
  const onVideoEndRef = useRef(onVideoEnd);
  const onCurrentTimeChangeRef = useRef(onCurrentTimeChange);
  const onSeekRef = useRef(onSeek);

  // Update refs when callbacks change
  useEffect(() => {
    onPlayStartRef.current = onPlayStart;
    onVideoEndRef.current = onVideoEnd;
    onCurrentTimeChangeRef.current = onCurrentTimeChange;
    onSeekRef.current = onSeek;
  }, [onPlayStart, onVideoEnd, onCurrentTimeChange, onSeek]);

  const videoId = useMemo(() => {
    if (!vimeoURL) return null;
    const urlParts = vimeoURL.split('/');
    return urlParts[3] || null;
  }, [vimeoURL]);

  useEffect(() => {
    if (!videoId) {
      setChapters([]);
      return;
    }
    getVideoChaptersByIdAPI({ videoId })
      .then(response => setChapters(response?.chapters || []))
      .catch(() => setChapters([]));
  }, [videoId]);

  const fetchDurationWithFallback = async (player, videoId) => {
    let duration = 0;
    let playerError = null;
    let apiError = null;

    //Method 1: Try player's getDuration() first
    try {
      duration = await player.getDuration();
      if (duration > 0) {
        setVideoDuration(duration);
        if (onDurationChange) {
          onDurationChange(duration);
        }
        setError(null);
        return duration;
      }
    } catch (error) {
      playerError = error;
      console.warn('Player getDuration failed, trying API fallback:', error.message);
    }

    // Method 2: Fallback to API if player failed or returned 0
    try {
      const result = await dispatch(fetchVideoDuration({ videoId }));
      if (result && result.duration > 0) {
        setVideoDuration(result.duration);
        if (onDurationChange) {
          onDurationChange(result.duration);
        }
        setError(null);
        return result.duration;
      }
    } catch (error) {
      apiError = error;
      console.error('API fetchVideoDuration also failed:', error.message);
    }

    // Both methods failed - set error state
    const errorMessage = `Failed to load video duration. Player error: ${playerError?.message ||
      'Unknown'}. API error: ${apiError?.message || 'Unknown'}`;
    console.error(errorMessage);
    setError('Unable to load video information. Please try refreshing the page.');

    return 0;
  };

  const debouncedOnTimeUpdate = useMemo(() => {
    return debounce((current, remaining) => {
      if (typeof onTimeUpdate === 'function') onTimeUpdate(current, remaining);
    }, 1000);
  }, [onTimeUpdate]);

  useEffect(() => () => debouncedOnTimeUpdate.cancel(), [debouncedOnTimeUpdate]);

  const ensurePlay = async player => {
    try {
      await player.play();
    } catch {
      try {
        await player.setMuted(true);
        await player.play();
      } catch {
        // ignore; user can tap play
      }
    }
  };

  useEffect(() => {
    if (!vimeoURL || !iframeRef.current || !videoId) return;

    const urlParts = vimeoURL.split('/');
    const privacyHash = urlParts[4] ? `?h=${urlParts[4]}` : '';
    const embedUrl = `${VIMEO_PLAYER_BASE_URL}/video/${videoId}${privacyHash}`;

    // Reset play start trigger for new video
    hasTriggeredPlayStartRef.current[videoId] = false;
    hasSignaledCompleteRef.current[videoId] = false;

    // Destroy previous player if exists
    if (playerRef.current) {
      const toDestroy = playerRef.current;
      playerRef.current = null;
      toDestroy.destroy().catch(() => {});
    }

    // Create new player
    const newPlayer = new Player(iframeRef.current, {
      url: embedUrl,
      autoplay: true,
      responsive: true,
    });

    playerRef.current = newPlayer;

    newPlayer.ready().then(async () => {
      if (!playerRef.current) return;

      // Start playback immediately
      await ensurePlay(newPlayer);

      // Fetch duration with fallback mechanism (player first, then API)
      await fetchDurationWithFallback(newPlayer, videoId);

      // Apply resume time ONLY ON FIRST LOAD for this videoId
      if (
        !hasAppliedResumeRef.current[videoId] &&
        typeof videoResumeTime === 'number' &&
        videoResumeTime >= 0
      ) {
        try {
          await newPlayer.setCurrentTime(videoResumeTime);
          hasAppliedResumeRef.current[videoId] = true; // mark as applied
          await ensurePlay(newPlayer);
        } catch {
          // ignore
        }
      }
    });

    // Handle play event to trigger onPlayStart
    newPlayer.on('play', async () => {
      if (!hasTriggeredPlayStartRef.current[videoId] && onPlayStartRef.current) {
        hasTriggeredPlayStartRef.current[videoId] = true;
        try {
          const currentTime = await newPlayer.getCurrentTime();
          const duration = await newPlayer.getDuration();
          onPlayStartRef.current(currentTime, duration);
        } catch (error) {
          console.error('Error getting time on play start:', error);
          // Fallback with current state values
          if (onPlayStartRef.current) {
            onPlayStartRef.current(0, videoDuration || 0);
          }
        }
      }
    });

    // Handle video end event
    newPlayer.on('ended', async () => {
      try {
        const currentTime = await newPlayer.getCurrentTime();
        const duration = await newPlayer.getDuration();
        if (onVideoEndRef.current) {
          onVideoEndRef.current(currentTime, duration);
        }
      } catch (error) {
        console.error('Error handling video end:', error);
      }
    });

    newPlayer.on('timeupdate', ({ seconds, duration }) => {
      debouncedOnTimeUpdate(seconds, Math.max(0, duration - seconds));
      updateCurrentChapter(seconds);

      // Always update currentTime and call onCurrentTimeChange for time tracking
      if (seconds !== currentTime) {
        setCurrentTime(seconds);
        const progressPercent = duration > 0 ? ((seconds / duration) * 100).toFixed(2) : 0;
        const minutes = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);

        if (onCurrentTimeChangeRef.current) {
          onCurrentTimeChangeRef.current(seconds, duration);
        }
      }
      // Proactively mark completion when within 1 second of the end (some players may not fire 'ended')
      if (
        duration > 0 &&
        seconds >= Math.max(0, duration - 1) &&
        !hasSignaledCompleteRef.current[videoId]
      ) {
        hasSignaledCompleteRef.current[videoId] = true;
        if (typeof onVideoEndRef.current === 'function') {
          try {
            onVideoEndRef.current(seconds, duration);
          } catch (e) {
            // ignore
          }
        }
      }
    });

    newPlayer.on('seeked', async () => {
      await ensurePlay(newPlayer);

      // Handle seek event for progress tracking
      if (onSeekRef.current) {
        try {
          const currentTime = await newPlayer.getCurrentTime();
          const duration = await newPlayer.getDuration();
          const progressPercent = duration > 0 ? ((currentTime / duration) * 100).toFixed(2) : 0;
          const minutes = Math.floor(currentTime / 60);
          const seconds = Math.floor(currentTime % 60);
          onSeekRef.current(currentTime, duration);
        } catch (error) {
          console.error('Error handling seek event:', error);
        }
      }
    });

    // Handle pause event to save progress
    newPlayer.on('pause', async () => {
      if (onCurrentTimeChangeRef.current) {
        try {
          const currentTime = await newPlayer.getCurrentTime();
          const duration = await newPlayer.getDuration();
          onCurrentTimeChangeRef.current(currentTime, duration);
        } catch (error) {
          console.error('Error handling pause event:', error);
        }
      }
    });

    newPlayer.on('chapterchange', data => setCurrentChapter(data.title));

    newPlayer.on('durationchange', ({ duration }) => {
      if (duration > 0 && duration !== videoDuration) {
        setVideoDuration(duration);
        if (onDurationChange) {
          onDurationChange(duration);
        }
      }
    });

    return () => {
      if (playerRef.current) {
        const toDestroy = playerRef.current;
        playerRef.current = null;
        // Remove all event listeners before destroying
        try {
          toDestroy.off('play');
          toDestroy.off('pause');
          toDestroy.off('ended');
          toDestroy.off('timeupdate');
          toDestroy.off('seeked');
          toDestroy.off('chapterchange');
          toDestroy.off('durationchange');
        } catch (e) {
          // Ignore errors when removing listeners
        }
        toDestroy.destroy().catch(() => {});
      }
    };
  }, [vimeoURL, videoId, debouncedOnTimeUpdate]);

  const updateCurrentChapter = currentTime => {
    const activeChapter = chapters.filter(ch => ch.time <= currentTime).slice(-1)[0];
    if (activeChapter && activeChapter.title !== currentChapter) {
      setCurrentChapter(activeChapter.title);
    }
  };

  useEffect(() => {
    if (playerRef.current && typeof seekToTime === 'number' && seekToTime >= 0) {
      playerRef.current
        .setCurrentTime(seekToTime)
        .then(() => {
          return ensurePlay(playerRef.current);
        })
        .catch(error => {
          console.error('Error seeking to time:', error);
        });
    }
  }, [seekToTime]);

  if (error) {
    return (
      <div className={css.errorContainer}>
        <p className={css.errorMessage}>{error}</p>
        <button
          className={css.retryButton}
          onClick={() => {
            setError(null);
            // Trigger re-initialization by forcing component remount
            window.location.reload();
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
      <div
        className={css.iframeContainer}
        ref={iframeRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: '#000',
        }}
      />
    </div>
  );
}

VimeoPlayer.propTypes = {
  vimeoURL: PropTypes.string.isRequired,
  onTimeUpdate: PropTypes.func,
  videoResumeTime: PropTypes.number,
  onDurationChange: PropTypes.func,
  onCurrentTimeChange: PropTypes.func,
  onPlayStart: PropTypes.func,
  onVideoEnd: PropTypes.func,
  onSeek: PropTypes.func,
  seekToTime: PropTypes.number,
};

export default VimeoPlayer;
